export const getCandleCountForTimeframe = (timeframe: string, totalCandles: number): number => {
  let optimalCandleCount = 100

  if (timeframe) {
    const tf = timeframe.toLowerCase()
    if (tf === 'm1') optimalCandleCount = 120
    else if (tf === 'm2') optimalCandleCount = 100
    else if (tf === 'm3') optimalCandleCount = 90
    else if (tf === 'm4') optimalCandleCount = 80
    else if (tf === 'm5') optimalCandleCount = 72
    else if (tf === 'm6') optimalCandleCount = 60
    else if (tf === 'm10') optimalCandleCount = 60
    else if (tf === 'm12') optimalCandleCount = 48
    else if (tf === 'm15') optimalCandleCount = 48
    else if (tf === 'm20') optimalCandleCount = 42
    else if (tf === 'm30') optimalCandleCount = 36
    else if (tf === 'h1') optimalCandleCount = 48
    else if (tf === 'h2') optimalCandleCount = 60
    else if (tf === 'h3') optimalCandleCount = 56
    else if (tf === 'h4') optimalCandleCount = 42
    else if (tf === 'h6') optimalCandleCount = 40
    else if (tf === 'h8') optimalCandleCount = 36
    else if (tf === 'h12') optimalCandleCount = 28
    else if (tf === 'd1') optimalCandleCount = 60
    else if (tf === 'w1') optimalCandleCount = 52
    else if (tf === 'mn1') optimalCandleCount = 24
  }

  return Math.min(optimalCandleCount, totalCandles)
}

export const getVisibleRangeForTimeframe = (timeframe: string, totalCandles: number): { from: number; to: number } => {
  const visibleCandles = getCandleCountForTimeframe(timeframe, totalCandles)

  const from = 0
  const to = Math.min(totalCandles - 1, from + visibleCandles - 1)

  return { from, to }
}

export const getInitialCandlesForTimeframe = (timeframe: string, totalCandles: number): number => {
  return getCandleCountForTimeframe(timeframe, totalCandles)
}

export const getInitialLoadPeriod = (startTimestamp: number): { start: number; end: number } => {
  const startDate = new Date(startTimestamp * 1000)
  startDate.setHours(0, 0, 0, 0)

  const endDate = new Date(startDate.getTime())
  endDate.setDate(startDate.getDate() + 14)
  endDate.setHours(23, 59, 59, 999)

  return {
    start: Math.floor(startDate.getTime() / 1000),
    end: Math.floor(endDate.getTime() / 1000),
  }
}

export const getCandleTimeRange = (timestamp: number, timeframe: string): { start: number; end: number } => {
  const date = new Date(timestamp * 1000)
  const startTime = new Date(date)
  const endTime = new Date(date)

  const tf = timeframe ? timeframe.toLowerCase() : 'm1'

  startTime.setSeconds(0, 0)
  endTime.setSeconds(59, 999)

  if (tf === 'm1') {
    // M1 uses the default seconds-level boundaries set above
  } else if (tf === 'm2') {
    const minute = Math.floor(startTime.getMinutes() / 2) * 2
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 1, 59, 999)
  } else if (tf === 'm3') {
    const minute = Math.floor(startTime.getMinutes() / 3) * 3
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 2, 59, 999)
  } else if (tf === 'm4') {
    const minute = Math.floor(startTime.getMinutes() / 4) * 4
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 3, 59, 999)
  } else if (tf === 'm5') {
    const minute = Math.floor(startTime.getMinutes() / 5) * 5
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 4, 59, 999)
  } else if (tf === 'm6') {
    const minute = Math.floor(startTime.getMinutes() / 6) * 6
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 5, 59, 999)
  } else if (tf === 'm10') {
    const minute = Math.floor(startTime.getMinutes() / 10) * 10
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 9, 59, 999)
  } else if (tf === 'm12') {
    const minute = Math.floor(startTime.getMinutes() / 12) * 12
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 11, 59, 999)
  } else if (tf === 'm15') {
    const minute = Math.floor(startTime.getMinutes() / 15) * 15
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 14, 59, 999)
  } else if (tf === 'm20') {
    const minute = Math.floor(startTime.getMinutes() / 20) * 20
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 19, 59, 999)
  } else if (tf === 'm30') {
    const minute = Math.floor(startTime.getMinutes() / 30) * 30
    startTime.setMinutes(minute, 0, 0)
    endTime.setMinutes(minute + 29, 59, 999)
  } else if (tf === 'h1') {
    startTime.setMinutes(0, 0, 0)
    endTime.setMinutes(59, 59, 999)
  } else if (tf === 'h2') {
    const hour = Math.floor(startTime.getHours() / 2) * 2
    startTime.setHours(hour, 0, 0, 0)
    endTime.setHours(hour + 1, 59, 59, 999)
  } else if (tf === 'h3') {
    const hour = Math.floor(startTime.getHours() / 3) * 3
    startTime.setHours(hour, 0, 0, 0)
    endTime.setHours(hour + 2, 59, 59, 999)
  } else if (tf === 'h4') {
    const hour = Math.floor(startTime.getHours() / 4) * 4
    startTime.setHours(hour, 0, 0, 0)
    endTime.setHours(hour + 3, 59, 59, 999)
  } else if (tf === 'h6') {
    const hour = Math.floor(startTime.getHours() / 6) * 6
    startTime.setHours(hour, 0, 0, 0)
    endTime.setHours(hour + 5, 59, 59, 999)
  } else if (tf === 'h8') {
    const hour = Math.floor(startTime.getHours() / 8) * 8
    startTime.setHours(hour, 0, 0, 0)
    endTime.setHours(hour + 7, 59, 59, 999)
  } else if (tf === 'h12') {
    const hour = Math.floor(startTime.getHours() / 12) * 12
    startTime.setHours(hour, 0, 0, 0)
    endTime.setHours(hour + 11, 59, 59, 999)
  } else if (tf === 'd1') {
    startTime.setHours(0, 0, 0, 0)
    endTime.setHours(23, 59, 59, 999)
  } else if (tf === 'w1') {
    const dayOfWeek = startTime.getDay()
    const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    startTime.setDate(startTime.getDate() - diffToMonday)
    startTime.setHours(0, 0, 0, 0)
    endTime.setDate(startTime.getDate() + 6)
    endTime.setHours(23, 59, 59, 999)
  } else if (tf === 'mn1') {
    startTime.setDate(1)
    startTime.setHours(0, 0, 0, 0)
    endTime.setMonth(endTime.getMonth() + 1, 0)
    endTime.setHours(23, 59, 59, 999)
  }

  return {
    start: Math.floor(startTime.getTime() / 1000),
    end: Math.floor(endTime.getTime() / 1000),
  }
}

/**
 * Bars to hold in the loaded window, as a multiple of what fits on screen.
 *
 * Window sizing used to be expressed in calendar days (M1→3, H1→7, D1→30)
 * because the old HTTP candle endpoint served one file per day. That only ever
 * worked for the three timeframes it supported: H4 with a 3-day buffer yields
 * 18 bars against a 42-bar viewport, which thrashes extendWindow forever. The
 * WebSocket datafeed answers an arbitrary range in one frame, so windows are
 * sized in bars and every timeframe gets a sane one.
 */
const WINDOW_SCREENS = 8
const PRUNE_FACTOR = 4

export const getWindowBarsForTimeframe = (timeframe: string): number =>
  Math.min(4000, Math.max(400, getCandleCountForTimeframe(timeframe, Infinity) * WINDOW_SCREENS))

/** Ceiling before the far side of the window is pruned. */
export const getMaxBarsForTimeframe = (timeframe: string): number =>
  getWindowBarsForTimeframe(timeframe) * PRUNE_FACTOR

export const formatDateLabel = (ts: number): string => {
  const d = new Date(ts * 1000)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const formatTimeLabel = (ts: number): string => {
  const d = new Date(ts * 1000)
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
}

const TIMEFRAME_MINUTES: Record<string, number> = {
  m1: 1, m2: 2, m3: 3, m4: 4, m5: 5, m6: 6,
  m10: 10, m12: 12, m15: 15, m20: 20, m30: 30,
  h1: 60, h2: 120, h3: 180, h4: 240, h6: 360, h8: 480, h12: 720,
  d1: 1440, w1: 10080, mn1: 43200,
}

/** Nominal bar duration in seconds. W1/MN1 are approximations, as on the server. */
export const getTimeframeSeconds = (timeframe: string): number => {
  const minutes = TIMEFRAME_MINUTES[(timeframe || 'm1').toLowerCase()]
  return (minutes ?? 1) * 60
}

/**
 * Whether `timeframe` names a bar size we actually know the duration of.
 *
 * getTimeframeSeconds silently falls back to M1, which is a fine default for
 * sizing a viewport but not for anything that subdivides a bar — placing steps
 * inside a bar of the wrong length would put them in the wrong place.
 */
export const isKnownTimeframe = (timeframe: string): boolean =>
  Boolean(TIMEFRAME_MINUTES[(timeframe || '').toLowerCase()])
